import { c as _c } from "react/compiler-runtime";
import type { ApiNodeDataVerifiedPrefill, CollectedDataOption, VerifiedChallengeKind } from '@onefootprint/request-types/dashboard';
import { Form } from '@onefootprint/ui';
import Link from 'next/link';
import { useEffect } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import useDirty from '@/components/playbook-builder/hooks/use-dirty';
import type { ApplyEditMutation } from '../../base-node';
import Section from '../../section';
import type { OutputMapping } from '../../shared/output-mappings';
import VerifiedPrefillChallengeKind, { DEFAULT_CHALLENGE_KIND } from '../../shared/verified-prefill-challenge-kind';
import VerifiedPrefillOutputs, { fromOutputMappings, toOutputMappings } from '../../shared/verified-prefill-outputs';
type VerifiedPrefillFormData = {
  challengeKind: VerifiedChallengeKind;
  skipIfFullAddressPopulated: boolean;
  skipIfSsnPopulated: boolean;
  outputs: OutputMapping[];
};
type VerifiedPrefillFormProps = {
  mutation: ApplyEditMutation;
  nodeData: ApiNodeDataVerifiedPrefill;
  nodeId: string;
  canEdit: boolean;
  isFirmEmployee: boolean;
};
const VerifiedPrefillForm = t0 => {
  const $ = _c(58);
  const {
    mutation,
    nodeData,
    nodeId,
    canEdit,
    isFirmEmployee
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "verifiedPrefill"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const {
    toggle
  } = useDirty();
  const t2 = nodeData.settings?.challengeKind ?? DEFAULT_CHALLENGE_KIND;
  let t3;
  if ($[1] !== nodeData.settings?.skipIfPopulated) {
    t3 = nodeData.settings?.skipIfPopulated?.includes("full_address") ?? true;
    $[1] = nodeData.settings?.skipIfPopulated;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== nodeData.settings?.skipIfPopulated) {
    t4 = nodeData.settings?.skipIfPopulated?.includes("ssn9") ?? true;
    $[3] = nodeData.settings?.skipIfPopulated;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = nodeData.settings?.outputs;
  let t6;
  if ($[5] !== t5) {
    t6 = toOutputMappings(t5);
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t6) {
    t7 = {
      defaultValues: {
        challengeKind: t2,
        skipIfFullAddressPopulated: t3,
        skipIfSsnPopulated: t4,
        outputs: t6
      }
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const methods = useForm(t7);
  let t8;
  if ($[12] !== methods.control) {
    t8 = {
      control: methods.control,
      name: "skipIfSsnPopulated"
    };
    $[12] = methods.control;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const skipIfSsnPopulatedDisabled = useWatch(t8);
  const {
    handleSubmit,
    formState: t9
  } = methods;
  const {
    isDirty
  } = t9;
  let t10;
  if ($[14] !== isDirty || $[15] !== toggle) {
    t10 = () => {
      toggle("verified-prefill", isDirty);
    };
    $[14] = isDirty;
    $[15] = toggle;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== isDirty) {
    t11 = [isDirty];
    $[17] = isDirty;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  useEffect(t10, t11);
  let t12;
  if ($[19] !== mutation || $[20] !== nodeId) {
    t12 = async data => {
      const skipIfPopulated = ["ssn9"];
      if (data.skipIfFullAddressPopulated) {
        skipIfPopulated.push("full_address");
      }
      ;
      try {
        await mutation.mutateAsync({
          nodeId,
          kind: "update_node",
          nodeData: {
            kind: "verified_prefill",
            settings: {
              challengeKind: data.challengeKind,
              skipIfPopulated,
              outputs: fromOutputMappings(data.outputs)
            }
          }
        });
      } catch (t13) {
        const _ = t13;
      }
    };
    $[19] = mutation;
    $[20] = nodeId;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const handleFormSubmit = t12;
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <VerifiedPrefillDisplay />;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== handleFormSubmit || $[24] !== handleSubmit) {
    t14 = handleSubmit(handleFormSubmit);
    $[23] = handleFormSubmit;
    $[24] = handleSubmit;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t) {
    t15 = t(_temp);
    $[26] = t;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== canEdit || $[29] !== isFirmEmployee) {
    t16 = <VerifiedPrefillChallengeKind canEdit={canEdit} isFirmEmployee={isFirmEmployee} />;
    $[28] = canEdit;
    $[29] = isFirmEmployee;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t) {
    t17 = t(_temp2);
    $[31] = t;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== t) {
    t18 = t(_temp3);
    $[33] = t;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  const t19 = !canEdit || skipIfSsnPopulatedDisabled;
  let t20;
  if ($[35] !== t17 || $[36] !== t18 || $[37] !== t19) {
    t20 = <Form.Toggle name="skipIfSsnPopulated" label={t17} hint={t18} size="compact" disabled={t19} />;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t) {
    t21 = t(_temp4);
    $[39] = t;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  const t22 = !canEdit;
  let t23;
  if ($[41] !== t21 || $[42] !== t22) {
    t23 = <Form.Toggle name="skipIfFullAddressPopulated" label={t21} size="compact" disabled={t22} />;
    $[41] = t21;
    $[42] = t22;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== canEdit) {
    t24 = <VerifiedPrefillOutputs canEdit={canEdit} />;
    $[44] = canEdit;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t15 || $[47] !== t16 || $[48] !== t20 || $[49] !== t23 || $[50] !== t24) {
    t25 = <Section title={t15} className="gap-3">{t16}{t20}{t23}{t24}</Section>;
    $[46] = t15;
    $[47] = t16;
    $[48] = t20;
    $[49] = t23;
    $[50] = t24;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t14 || $[53] !== t25) {
    t26 = <form id="verified-prefill-form" onSubmit={t14}>{t25}</form>;
    $[52] = t14;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== methods || $[56] !== t26) {
    t27 = <FormProvider {...methods}>{t13}{t26}</FormProvider>;
    $[55] = methods;
    $[56] = t26;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  return t27;
};
const VerifiedPrefillDisplay = () => {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "verifiedPrefill"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t0);
  let t1;
  if ($[1] !== t) {
    t1 = t(_temp5);
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-body-2 text-tertiary"><Trans ns="playbook-builder" i18nKey={_temp6} components={{
        code: <code className="text-snippet-2 px-1 rounded bg-secondary" />,
        verifiedLink: <Link href="https://verified.inc" target="_blank" className="text-accent" rel="noopener noreferrer" />
      }} /></p>;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1) {
    t3 = <Section title={t1} className="gap-3">{t2}</Section>;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};
export default VerifiedPrefillForm;
function _temp($) {
  return $.settings;
}
function _temp2($_0) {
  return $_0.form.skipSsn.label;
}
function _temp3($_1) {
  return $_1.form.skipSsn.tooltip;
}
function _temp4($_2) {
  return $_2.form.skipFullAddress.label;
}
function _temp5($) {
  return $.aboutThisNode;
}
function _temp6($_0) {
  return $_0.verifiedPrefill.description;
}